import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import SchoolIcon from '@mui/icons-material/School';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import {
  createPersonalProfilerAction,
  getAllCompanyProprietorsAction,
  getAllQualificationAction,
  getAllRealtionAction,
  getPersonalProfilerAction,
  patchPersonalProfilerAction,
} from '../action';
import { toast, ToastContainer } from 'react-toastify';
import { OperationType } from '../../../utils/settings';

function FamilyDetails({ expanded = false }) {
  const [familyDetailsList, setFamilyDetailsList] = useState([]);
  const [familyDetails, setFamilyDetails] = useState({});
  const [open, setOpen] = React.useState(false);
  const [ownersList, setOwnersList] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [proprietorsList, setProprietorsList] = useState([
    {
      firm_info: {},
      proprietors_list: [],
    },
  ]);
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [relationOptions, setRelationOptions] = useState([]);
  const [allowAdd, setAllowAdd] = useState(true);
  const [proprietorsFlatList, setProprietorsFlatList] = useState([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFamilyDetails({});
    setIsEditing(false);
    setIsAddingMember(false);
  };

  useEffect(() => {
    const flatProprietorsList = Array.from(
      new Set(
        proprietorsList.flatMap((item) =>
          item.proprietors_list.map((prop) => JSON.stringify(prop)),
        ),
      ),
    ).map((str) => JSON.parse(str));

    setProprietorsFlatList(flatProprietorsList);

    if (familyDetailsList.length > 0) {
      setAllowAdd(familyDetailsList?.length < flatProprietorsList.length);
    }
  }, [proprietorsList, familyDetailsList]);

  const getPersonalProfiler = async () => {
    const { data, error, message } = await getPersonalProfilerAction();

    if (!error) {
      setFamilyDetailsList(data.family_details_info);
    } else toast.error(message);
  };

  const getProprietorsList = async () => {
    const { data, error, message } = await getAllCompanyProprietorsAction();

    if (!error) setProprietorsList(data);
    else toast.error(message);
  };

  const getQualifications = async () => {
    const { data, error, message } = await getAllQualificationAction();

    if (!error) setQualificationOptions(data);
    else toast.error(message);
  };

  const getRelations = async () => {
    const { data, error, message } = await getAllRealtionAction();

    if (!error) setRelationOptions(data);
    else toast.error(message);
  };

  useEffect(() => {
    if (expanded) {
      getPersonalProfiler();
      getProprietorsList();
      getQualifications();
      getRelations();
    }
  }, [expanded]);

  const onFinish = async () => {
    if(!familyDetails.firmDetails) return toast.error('Please Select Firm');
    if(!familyDetails.ownerDetails) return toast.error('Please Select Owner');
    if(!familyDetails.name) return toast.error('Please Enter Name');
    if(!familyDetails.relation) return toast.error('Please Select Relation');
    if(!familyDetails.date_of_birth) return toast.error('Please Select Date of Birth');
    
    const familyPayload = {
      firm_id: familyDetails.firmDetails.id,
      owner_id: familyDetails.ownerDetails.id,
      family_members_list: [
        {
          name: familyDetails.name,
          relation: familyDetails.relation,
          qualification: familyDetails.qualification,
          date_of_birth: dayjs(familyDetails.date_of_birth).format(
            'YYYY-MM-DD',
          ),
        },
      ],
    };

    const finalPayload = {
      personal_details_info: [],
      family_details_info: [familyPayload],
      recreation_details_info: [],
    };

    const { error, message } = await createPersonalProfilerAction(finalPayload);

    if (!error) {
      toast.success(message);
      handleClose();
      getPersonalProfiler();
    } else {
      toast.error(message);
    }
  };

  const onEdit = (item, obj) => {
    const { firm_info, owner_info } = obj;

    const payload = {
      firmDetails: firm_info,
      ownerDetails: owner_info,
      name: item.name,
      relation: item.relation,
      qualification: item.qualification,
      date_of_birth: dayjs(item.date_of_birth),
      id: item.id,
    };

    const value = firm_info.id;
    const found = proprietorsList.find((item) => item.firm_info.id === value);
    setOwnersList(found.proprietors_list);

    setFamilyDetails(payload);
    setIsEditing(true);
    handleOpen();
  };

  const onUpdate = async () => {
    const payload = {
      family_details_info: [
        {
          firm_id: familyDetails.firmDetails.id,
          owner_id: familyDetails.ownerDetails.id,
          family_members_info: [
            {
              id: familyDetails.id,
              name: familyDetails.name,
              relation: familyDetails.relation,
              qualification: familyDetails.qualification,
              date_of_birth: dayjs(familyDetails.date_of_birth).format(
                'YYYY-MM-DD',
              ),
              operation_type: OperationType['UPDATE'],
            },
          ],
        },
      ],
    };

    const { error, message } = await patchPersonalProfilerAction(payload);

    if (!error) {
      toast.success(message);
      handleClose();
      getPersonalProfiler();
    } else {
      toast.error(message);
    }
  };

  const onAddMember = (data) => {
    const { firm_info, owner_info } = data;

    const payload = {
      firmDetails: firm_info,
      ownerDetails: owner_info,
    };

    const value = firm_info.id;
    const found = proprietorsList.find((item) => item.firm_info.id === value);
    setOwnersList(found.proprietors_list);

    setFamilyDetails(payload);
    setIsAddingMember(true);
    handleOpen();
  };

  const onAddMemberFinish = async () => {
    const payload = {
      family_details_info: [
        {
          firm_id: familyDetails.firmDetails.id,
          owner_id: familyDetails.ownerDetails.id,
          family_members_info: [
            {
              name: familyDetails.name,
              relation: familyDetails.relation,
              qualification: familyDetails.qualification,
              date_of_birth: dayjs(familyDetails.date_of_birth).format(
                'YYYY-MM-DD',
              ),
              operation_type: OperationType['ADD'],
            },
          ],
        },
      ],
    };

    const { error, message } = await patchPersonalProfilerAction(payload);

    if (!error) {
      toast.success(message);
      handleClose();
      getPersonalProfiler();
    } else {
      toast.error(message);
    }
  };

  return (
    <div>
      <ToastContainer className="custom-toast-container" />
      <CardContent>
        {familyDetailsList.map((item) => (
          <Accordion
            sx={{
              marginBottom: 1.5,
            }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='panel1-content'
              id='panel1-header'
              sx={{
                padding: 1,
                paddingLeft: 2,
                paddingRight: 2,
              }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}>
                <Typography fontWeight={'600'}>
                  {item.owner_info.name} | {item.firm_info.name}
                </Typography>

                <Button
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddMember(item);
                  }}
                  variant='outlined'
                  style={{
                    marginRight: 10,
                  }}>
                  <Typography>Add Member</Typography>
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '20px',
                }}>
                {item.family_members_info.map((fam) => (
                  <Box
                    sx={{
                      borderWidth: 2,
                      borderColor: '#F59C1E',
                      borderStyle: 'solid',
                      padding: 2,
                      borderRadius: 2,
                      marginBottom: 3,
                    }}>
                    <div
                      className='row-center'
                      style={{
                        justifyContent: 'space-between',
                        marginBottom: 5,
                        position: 'relative',
                      }}>
                      <div
                        className='editCtr'
                        style={{ position: 'absolute', right: -30, top: -35 }}
                        onClick={() => onEdit(fam, item)}>
                        <Tooltip title='Edit Details'>
                          <EditIcon color='primary' />
                        </Tooltip>
                      </div>
                      <div className='row-center'>
                        <Typography
                          fontSize={15}
                          fontWeight={'600'}
                          sx={{ marginRight: 1 }}>
                          {fam.name}
                        </Typography>
                        |
                        <Typography
                          fontSize={15}
                          fontWeight={'600'}
                          sx={{ marginLeft: 1 }}>
                          {fam.relation}
                        </Typography>
                      </div>

                      <div className='row-center'>
                        <div className='row-center'>
                          <SchoolIcon
                            color='info'
                            style={{
                              marginRight: 10,
                            }}
                            sx={{ marginLeft: 1 }}
                          />
                          <Typography fontSize={15}>
                            {fam.qualification}
                          </Typography>
                        </div>
                      </div>
                    </div>

                    <div
                      className='row-center'
                      style={{
                        justifyContent: 'flex-end',
                      }}>
                      <div className='row-center'>
                        <Typography
                          fontSize={15}
                          sx={{ marginRight: 1 }}>
                          Date Of Birth:
                        </Typography>
                        <Typography fontSize={15}>
                          {fam.date_of_birth}
                        </Typography>
                      </div>
                    </div>
                  </Box>
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>

      {allowAdd && (
        <div
          className='row-center'
          style={{
            justifyContent: 'flex-end',
          }}>
          <div>
            <Button
              onClick={() => {
                if (proprietorsFlatList.length === 0)
                  return toast.error('Please add owners and then fill details');
                handleOpen();
              }}
              sx={{ width: 150 }}
              variant='contained'>
              Add
            </Button>
          </div>
        </div>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'>
        <Box
          sx={{
            backgroundColor: 'white',
            width: '50%',
            overflowY: 'scroll',
            maxHeight: '90vh',
            borderRadius: '10px',
            padding: '20px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}>
          <CardContent>
            <div className='title-Ctr'>
              <Typography
                variant='h5'
                fontWeight={'600'}>
                {' '}
                {isEditing ? 'Update Family Details' : 'Add Family Details'}
              </Typography>
            </div>
            <div className='form-row-ctr'>
              <div className='form-row-ctr-left'>
                <Typography
                  variant='h6'
                  fontSize={15}>
                  Select Firm
                </Typography>
              </div>
              <div className='form-row-ctr-right'>
                <FormControl
                  disabled={isEditing || isAddingMember}
                  fullWidth
                  margin='normal'>
                  <InputLabel id='demo-simple-select-label'>
                    Select Firm
                  </InputLabel>
                  <Select
                    labelId='demo-simple-select-label'
                    id='demo-simple-select'
                    value={familyDetails?.firmDetails?.id}
                    label='Select Firm'
                    onChange={(e) => {
                      const value = e.target.value;
                      const found = proprietorsList.find(
                        (item) => item.firm_info.id === value,
                      );

                      setFamilyDetails((prev) => ({
                        ...prev,
                        firmDetails: found.firm_info,
                      }));
                      setOwnersList(found.proprietors_list);
                    }}>
                    {proprietorsList.map((item) => (
                      <MenuItem value={item.firm_info.id}>
                        {item.firm_info.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className='form-row-ctr'>
              <div className='form-row-ctr-left'>
                <Typography
                  variant='h6'
                  fontSize={15}>
                  Select Owner
                </Typography>
              </div>
              <div className='form-row-ctr-right'>
                <FormControl
                  disabled={isEditing || isAddingMember}
                  fullWidth
                  margin='normal'>
                  <InputLabel id='demo-simple-select-label'>
                    Select Owner
                  </InputLabel>
                  <Select
                    labelId='demo-simple-select-label'
                    id='demo-simple-select'
                    value={familyDetails?.ownerDetails?.id}
                    label='Select Owner'
                    onChange={(e) => {
                      const value = e.target.value;
                      const found = ownersList.find(
                        (item) => item.id === value,
                      );
                      setFamilyDetails((prev) => ({
                        ...prev,
                        ownerDetails: found,
                      }));
                    }}>
                    {ownersList.map((item) => (
                      <MenuItem value={item.id}>{item.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className='form-row-ctr'>
              <div className='form-row-ctr-left'>
                <Typography
                  variant='h6'
                  fontSize={15}>
                  Family Member Name
                </Typography>
              </div>
              <div className='form-row-ctr-right'>
                <TextField
                  label='Name'
                  name='name'
                  value={familyDetails.name}
                  onChange={(e) => {
                    setFamilyDetails((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }));
                  }}
                  fullWidth
                  margin='normal'
                />
              </div>
            </div>

            <div className='form-row-ctr'>
              <div className='form-row-ctr-left'>
                <Typography
                  variant='h6'
                  fontSize={15}>
                  Family Member Relation
                </Typography>
              </div>
              <div className='form-row-ctr-right'>
                <FormControl
                  fullWidth
                  margin='normal'>
                  <InputLabel id='demo-simple-select-label'>
                    Select Relation
                  </InputLabel>
                  <Select
                    labelId='demo-simple-select-label'
                    id='demo-simple-select'
                    value={familyDetails.relation}
                    label='Select Relation'
                    onChange={(e) => {
                      const value = e.target.value;
                      setFamilyDetails((prev) => ({
                        ...prev,
                        relation: value,
                      }));
                    }}>
                    {relationOptions.map((item) => (
                      <MenuItem value={item}>{item}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className='form-row-ctr'>
              <div className='form-row-ctr-left'>
                <Typography
                  variant='h6'
                  fontSize={15}>
                  Family Member Qualification
                </Typography>
              </div>
              <div className='form-row-ctr-right'>
                <FormControl
                  fullWidth
                  margin='normal'>
                  <InputLabel id='demo-simple-select-label'>
                    Select Qualification
                  </InputLabel>
                  <Select
                    labelId='demo-simple-select-label'
                    id='demo-simple-select'
                    value={familyDetails.qualification}
                    label='Select Qualification'
                    onChange={(e) => {
                      const value = e.target.value;
                      setFamilyDetails((prev) => ({
                        ...prev,
                        qualification: value,
                      }));
                    }}>
                    {qualificationOptions.map((item) => (
                      <MenuItem value={item}>{item}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className='form-row-ctr'>
              <div className='form-row-ctr-left'>
                <Typography
                  variant='h6'
                  fontSize={15}>
                  Family Member D.O.B
                </Typography>
              </div>
              <div className='form-row-ctr-right'>
                <DatePicker
                  label='Select Date'
                  fullWidth
                  maxDate={dayjs()}
                  value={dayjs(familyDetails?.date_of_birth)}
                  onChange={(e) => {
                    setFamilyDetails((prev) => ({
                      ...prev,
                      date_of_birth: e,
                    }));
                  }}
                  sx={{
                    marginTop: '16px',
                    marginBottom: '8px',
                    width: '100%',
                  }}
                />
              </div>
            </div>

            <div
              className='row-center'
              style={{
                justifyContent: 'flex-end',
              }}>
              <div>
                {/*  */}
                <Button
                  onClick={handleClose}
                  sx={{ width: 150, marginTop: 2, marginRight: 2 }}
                  variant='contained'
                  color='error'>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (isAddingMember) onAddMemberFinish();
                    else if (isEditing) onUpdate();
                    else onFinish();
                  }}
                  sx={{ width: 150, marginTop: 2 }}
                  variant='contained'>
                  Save
                </Button>
              </div>
            </div>
          </CardContent>
        </Box>
      </Modal>
    </div>
  );
}

export default FamilyDetails;
